import {
    Badge,
    Box,
    Flex,
    Button,
    SimpleGrid,
    SkeletonText,
    Spinner,
    Stat,
    StatLabel,
    StatNumber,
    Text,
    Tooltip,
    useColorModeValue
} from '@chakra-ui/react';
import AppTableForAnalytics from 'components/AppTableForAnalytics';
import AppTableWithPagination from 'components/AppTableWithPagination';
import SidebarWithHeader from 'components/SidebarWithHeader';
import currencySymbolMap from 'currency-symbol-map';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { TbRefresh } from 'react-icons/tb';
import { useGetActiveCampaignsQuery, useGetAdAccountsCampaignsQuery, useGetAdspressoAdaccountsQuery, useGetAdspressoCampaignsLaunchedQuery, useGetAdspressoUsersQuery, useGetTotalSpendQuery, useGetUpdateTracklistMutation } from 'store/adspresso/adspressoAnalyticsApi';

function Analytics() {
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState([]);

    const { data: overallAdsAccountsCampaigns, error: overallAdsAccountsCampaignsError, isLoading: isOverallAdsAccountsCampaignsLoading, isFetching, refetch } = useGetAdAccountsCampaignsQuery({ currentPage, filters }, {
        pollingInterval: 30000,
        skipPollingIfUnfocused: true,
    });

    const { data: adspressoUsers, isLoading: isAdspressoUsersLoading } = useGetAdspressoUsersQuery();
    const { data: adspressoAdsAccounts, isLoading: isAdspressoAdsAccountsLoading } = useGetAdspressoAdaccountsQuery();
    const { data: campaignsLaunched, isLoading: isCampaignsLaunchedLoading } = useGetAdspressoCampaignsLaunchedQuery();
    const { data: activeCampaigns, isLoading: isActiveCampaignsLoading } = useGetActiveCampaignsQuery();
    const { data: totalSpend, isLoading: isToralSpendLoading } = useGetTotalSpendQuery({ filters });
    
    const [triggerUpdateTracklist, { isLoading: isUpdateTracklistLoading }] = useGetUpdateTracklistMutation();

    const handleSearchInputChange = (searchFilters) => {
        setFilters(searchFilters);
    };


    const accountColumns = useMemo(
        () => [
            {
                Header: 'Ad Account',
                accessor: 'adsAccountName',
                Filter: '',
                filter: '',
                Cell: ({ row: { original } }) => {

                    return (
                        <>
                        <Flex direction={'column'} mb={-4}>
                            <Text fontSize={'14px'}>{original.adsAccountName}</Text>
                            <Text fontSize={'10px'} color={'gray.500'}>{original.adsAccountId}</Text>
                        </Flex>
                        </>
                    )
                }
            },
            {
                Header: 'campaign',
                accessor: 'campaign',
                Filter: '',
                filter: '',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {

                    return (
                        <>
                        <Flex direction={'column'} mb={-4}>
                            <Text fontSize={'14px'}>{original?.campaign?.name}</Text>
                            <Text fontSize={'10px'} color={'gray.500'}>{original?.campaign?.campaign_id}</Text>
                        </Flex>
                        </>
                    )
                }
            },
            {
                Header: 'Spend',
                accessor: '',
                Filter: '',
                filter: '',
                Cell: ({ row: { original } }) => {
                    const symbol = currencySymbolMap(original.currency) || original.currency;

                    return (
                        <Text fontSize={'14px'}>
                            {original.spend ? `${symbol}${original.spend}` : '-'}
                        </Text>
                    );
                }
            },
            {
                Header: 'Campaign status',
                accessor: '',
                Filter: '',
                filter: '',
                Cell: ({ row: { original } }) => (<Text fontSize={'14px'}>{original.campaign_status ? original.campaign_status : '-'}</Text>)
            },
            {
                Header: 'Client',
                accessor: '',
                Filter: '',
                filter: '',
                Cell: ({ row: { original } }) => (<Text fontSize={'14px'}>{original.clientId?.name}</Text>)
            },
            {
                Header: 'status',
                accessor: 'status',
                Filter: '',
                filter: '',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
  
                    return (
                        <>
                            <Badge
                                fontSize={"10px"}
                                marginRight={2}
                                colorScheme={original.status == 'success' ? 'green' : 'gray'}
                            >
                                {original.status}
                            </Badge>
                        </>
                    )
                }
            },
            {
                Header: 'modified date',
                accessor: '',
                Filter: '',
                filter: '',
                Cell: ({ row: { original } }) => (<Text fontSize={'14px'}>{original.createdDate ? moment(original.createdDate).format("DD-MM-YYYY hh:mm A") : '-'}</Text>)
            },
        ],
        []
    )


    return (
        <SidebarWithHeader>

            <Flex direction="column" justifyContent={'space-between'} minH={'120px'} gap={4}>
                <>
                    {(isAdspressoUsersLoading || isAdspressoAdsAccountsLoading || isCampaignsLaunchedLoading || isActiveCampaignsLoading || isToralSpendLoading) ? (
                        <Spinner mt={'20px'} />
                    ) : (
                        <Flex direction="column" gap={6}>
                                {/* First row of Stats */}
                                <SimpleGrid templateColumns="repeat(4, 1fr)" spacing={{ base: 5, lg: 8 }} w="100%">
                                    <StatsCard title="Connected Users" stat={adspressoUsers ? adspressoUsers.result : '-'} />
                                    <StatsCard title="Total Ad Accounts" stat={adspressoAdsAccounts ? adspressoAdsAccounts[0]?.Adaccounts : '-'} />
                                    <StatsCard 
                                        title="Campaigns Launched" 
                                        stat={
                                            campaignsLaunched 
                                            ? campaignsLaunched.find(item => item.action === "createCampaign")?.count 
                                            : '-'
                                        } 
                                    />
                                    <StatsCard title="Active Campaigns" stat={activeCampaigns} />
                                </SimpleGrid>

                                {/* Second row: Spend per Currency */}
                                {isUpdateTracklistLoading ? (
                                    <Flex>
                                       <SkeletonText width="80%" noOfLines={3} skeletonHeight={2} />
                                    </Flex>
                                ) : totalSpend.length > 0 ? (
                                    <SimpleGrid templateColumns="repeat(4, 1fr)" spacing={{ base: 5, lg: 8 }} w="100%">
                                        {totalSpend.map((item, idx) => {
                                            let symbol = currencySymbolMap(item.currency) || item.currency;

                                                // Specifically handle CAD currency
                                                if (item.currency === 'CAD') {
                                                    symbol = 'C$';
                                                }

                                            return (
                                                <StatsCard
                                                    key={idx}
                                                    title={`Total Spend (${item.currency})`}
                                                    stat={
                                                        parseFloat(item.totalSpend) === 0
                                                            ? `${symbol}0`
                                                            : `${symbol}${parseFloat(item.totalSpend).toFixed(2)}`
                                                    }
                                                />
                                            );
                                        })}
                                    </SimpleGrid>
                                ) : (
                                    null
                                )}
                            </Flex>

                    )}
                </>
                <Flex justifyContent={'flex-end'}>
                    <Button
                        colorScheme='gray'
                        onClick={() => triggerUpdateTracklist()}
                        size="xs"
                        leftIcon={<TbRefresh />}
                    >
                        Refresh
                    </Button>
                </Flex>
            </Flex>
        
            {
                isOverallAdsAccountsCampaignsLoading ? (
                    <Flex minH={'400px'} alignItems={'center'} justifyContent={'center'}>
                        <Spinner mt={'20px'} />
                    </Flex>

                ) : overallAdsAccountsCampaignsError ? 
                    <Flex justifyContent={'center'} alignItems={'center'} height={'50vh'}>
                         <Text color="red.500">
                             {overallAdsAccountsCampaignsError?.data?.message}
                         </Text>
                     </Flex>
                 : (
                    <Box mt={'20px'}>
                        <>
                            <AppTableForAnalytics
                                columns={accountColumns}
                                data={overallAdsAccountsCampaigns?.data}
                                paginationMeta={overallAdsAccountsCampaigns?.meta}
                                onPageChange={(index) => setCurrentPage(index)}
                                isFetching={isFetching}
                                onSearchInputChange={handleSearchInputChange}
                                searchEnabled={true}
                            />
                        </>
                    </Box>
                )
            }


        </SidebarWithHeader >
    )
}

function StatsCard(props) {
    const { title, stat } = props
    return (
        <Stat
            px={{ base: 2, md: 8 }}
            py={'2'}
            //shadow={'md'}
            border={'1px solid'}
            borderColor={useColorModeValue('gray.800', 'gray.500')}
            rounded={'lg'}>
            <StatLabel fontWeight={'medium'} isTruncated>
                {title}
            </StatLabel>
            <StatNumber fontSize={'xl'} fontWeight={'bold'}>
                {stat}
            </StatNumber>
        </Stat>
    )
}

export default Analytics